/**
 * Firebase Auth Composable
 *
 * Provides reactive state and methods for Firebase Authentication.
 * Handles login, registration, OAuth, password reset, and session management.
 */

import { ref, computed, onMounted, onUnmounted, type Ref, type ComputedRef } from 'vue';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  type User,
  type AuthProvider as FirebaseAuthProvider,
  type Unsubscribe,
} from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { api, type SessionUser } from '../services/api';
import type {
  FirebaseUser,
  AuthState,
  AuthError,
  LoginCredentials,
  RegisterCredentials,
  UserProfile,
  AuthProvider,
  AUTH_ERROR_MESSAGES,
} from '../types/firebase-auth.types';

// =============================================================================
// Firebase Configuration
// =============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Check if Firebase is configured
const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId
);

// Initialize Firebase only if configured (singleton)
let app: ReturnType<typeof initializeApp> | null = null;
let auth: ReturnType<typeof getAuth> | null = null;

if (isFirebaseConfigured) {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  auth = getAuth(app);
} else {
  console.warn('[Firebase Auth] Not configured. Set VITE_FIREBASE_* env variables to enable authentication.');
}

// =============================================================================
// Types
// =============================================================================

interface UseFirebaseAuthReturn {
  // State
  user: Ref<FirebaseUser | null>;
  authState: Ref<AuthState>;
  loading: Ref<boolean>;
  error: Ref<AuthError | null>;
  sessionUser: Ref<SessionUser | null>;
  hasBackendSession: ComputedRef<boolean>;

  // Computed
  isAuthenticated: ComputedRef<boolean>;
  isEmailVerified: ComputedRef<boolean>;
  isAnonymous: ComputedRef<boolean>;
  userDisplayName: ComputedRef<string>;
  userEmail: ComputedRef<string | null>;
  userPhotoURL: ComputedRef<string | null>;
  userId: ComputedRef<string | null>;

  // Auth Methods
  loginWithEmail: (credentials: LoginCredentials) => Promise<boolean>;
  registerWithEmail: (credentials: RegisterCredentials) => Promise<boolean>;
  loginWithOAuth: (provider: AuthProvider) => Promise<boolean>;
  logout: () => Promise<void>;

  // Backend Session Management
  createBackendSession: (deviceName?: string) => Promise<boolean>;
  refreshBackendSession: () => Promise<boolean>;

  // Password Management
  resetPassword: (email: string) => Promise<boolean>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<boolean>;

  // Email Verification
  sendVerificationEmail: () => Promise<boolean>;
  isEmailVerificationPending: ComputedRef<boolean>;

  // Profile Management
  updateUserProfile: (profile: UserProfile) => Promise<boolean>;
  updateUserEmail: (newEmail: string, password: string) => Promise<boolean>;

  // Token Management
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;

  // Utility
  clearError: () => void;
  getErrorMessage: (code: string) => string;
}

// =============================================================================
// Singleton State
// =============================================================================

const user = ref<FirebaseUser | null>(null);
const authState = ref<AuthState>('loading');
const loading = ref(false);
const error = ref<AuthError | null>(null);
const sessionUser = ref<SessionUser | null>(null);
let unsubscribe: Unsubscribe | null = null;
let sessionRefreshHandler: ((e: Event) => void) | null = null;

// =============================================================================
// Helper Functions
// =============================================================================

function formatUser(firebaseUser: User | null): FirebaseUser | null {
  if (!firebaseUser) return null;

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    emailVerified: firebaseUser.emailVerified,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    phoneNumber: firebaseUser.phoneNumber,
    isAnonymous: firebaseUser.isAnonymous,
    metadata: {
      creationTime: firebaseUser.metadata.creationTime ?? null,
      lastSignInTime: firebaseUser.metadata.lastSignInTime ?? null,
    },
    providerData: firebaseUser.providerData.map((provider) => ({
      providerId: provider.providerId,
      uid: provider.uid,
      displayName: provider.displayName,
      email: provider.email,
      phoneNumber: provider.phoneNumber,
      photoURL: provider.photoURL,
    })),
  };
}

function getOAuthProvider(providerId: AuthProvider): FirebaseAuthProvider {
  switch (providerId) {
    case 'google.com':
      return new GoogleAuthProvider();
    case 'github.com':
      return new GithubAuthProvider();
    case 'apple.com': {
      const appleProvider = new OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');
      return appleProvider;
    }
    case 'microsoft.com': {
      const msProvider = new OAuthProvider('microsoft.com');
      msProvider.addScope('email');
      msProvider.addScope('profile');
      return msProvider;
    }
    case 'facebook.com': {
      const fbProvider = new OAuthProvider('facebook.com');
      fbProvider.addScope('email');
      return fbProvider;
    }
    case 'twitter.com':
      return new OAuthProvider('twitter.com');
    default:
      throw new Error(`Unsupported OAuth provider: ${providerId}`);
  }
}

function handleAuthError(err: unknown): AuthError {
  const firebaseError = err as { code?: string; message?: string };
  return {
    code: firebaseError.code ?? 'auth/unknown-error',
    message: firebaseError.message ?? 'An unexpected error occurred',
  };
}

const ERROR_MESSAGES: Record<string, string> = {
  'auth/not-configured': 'Authentication is not configured. Contact support.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/operation-not-allowed': 'This operation is not allowed.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',
  'auth/credential-already-in-use': 'This credential is already linked to another account.',
};

// =============================================================================
// Composable
// =============================================================================

export function useFirebaseAuth(): UseFirebaseAuthReturn {
  // =============================================================================
  // Computed Properties
  // =============================================================================

  const isAuthenticated = computed(() => {
    return authState.value === 'authenticated' && user.value !== null;
  });

  const isEmailVerified = computed(() => {
    return user.value?.emailVerified ?? false;
  });

  const isAnonymous = computed(() => {
    return user.value?.isAnonymous ?? false;
  });

  const userDisplayName = computed(() => {
    return user.value?.displayName ?? user.value?.email?.split('@')[0] ?? 'User';
  });

  const userEmail = computed(() => {
    return user.value?.email ?? null;
  });

  const userPhotoURL = computed(() => {
    return user.value?.photoURL ?? null;
  });

  const userId = computed(() => {
    return user.value?.uid ?? null;
  });

  const isEmailVerificationPending = computed(() => {
    return isAuthenticated.value && !isEmailVerified.value && !isAnonymous.value;
  });

  const hasBackendSession = computed(() => {
    return api.hasSession();
  });

  // =============================================================================
  // Auth State Listener
  // =============================================================================

  function setupAuthListener(): void {
    if (unsubscribe) return; // Already listening
    if (!auth) {
      authState.value = 'unauthenticated';
      return;
    }

    unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      user.value = formatUser(firebaseUser);
      authState.value = firebaseUser ? 'authenticated' : 'unauthenticated';
    });
  }

  // =============================================================================
  // Email/Password Authentication
  // =============================================================================

  async function loginWithEmail(credentials: LoginCredentials): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      user.value = formatUser(result.user);
      authState.value = 'authenticated';

      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function registerWithEmail(credentials: RegisterCredentials): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      // Update display name if provided
      if (credentials.displayName) {
        await updateProfile(result.user, {
          displayName: credentials.displayName,
        });
      }

      // Send email verification
      await sendEmailVerification(result.user);

      user.value = formatUser(result.user);
      authState.value = 'authenticated';

      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // OAuth Authentication
  // =============================================================================

  async function loginWithOAuth(providerId: AuthProvider): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const provider = getOAuthProvider(providerId);
      const result = await signInWithPopup(auth, provider);

      user.value = formatUser(result.user);
      authState.value = 'authenticated';

      return true;
    } catch (err) {
      error.value = handleAuthError(err);

      // If popup was blocked, try redirect
      if (error.value.code === 'auth/popup-blocked') {
        try {
          const provider = getOAuthProvider(providerId);
          await signInWithRedirect(auth, provider);
          return true;
        } catch (redirectErr) {
          error.value = handleAuthError(redirectErr);
        }
      }

      return false;
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // Backend Session Management
  // =============================================================================

  /**
   * Create a backend session after Firebase authentication
   * This should be called after successful login to enable session-based auth
   */
  async function createBackendSession(deviceName?: string): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    try {
      const firebaseToken = await getIdToken(true);
      if (!firebaseToken) {
        error.value = { code: 'auth/no-token', message: 'Could not get Firebase token' };
        return false;
      }

      const response = await api.createSession(firebaseToken, deviceName);
      if (response.success && response.data) {
        sessionUser.value = response.data.user;
        return true;
      }

      error.value = { code: 'session/creation-failed', message: response.error ?? 'Failed to create session' };
      return false;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    }
  }

  /**
   * Refresh the backend session with a fresh Firebase token
   */
  async function refreshBackendSession(): Promise<boolean> {
    if (!auth) return false;

    try {
      const firebaseToken = await getIdToken(true);
      if (!firebaseToken) return false;

      const response = await api.refreshSession(firebaseToken);
      if (response.success && response.data) {
        sessionUser.value = response.data.user;
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // =============================================================================
  // Logout
  // =============================================================================

  async function logout(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      // Revoke backend session first (if one exists)
      if (api.hasSession()) {
        try {
          await api.revokeSession();
        } catch {
          // Continue with logout even if session revocation fails
        }
      }

      // Clear session data
      sessionUser.value = null;
      api.clearSession();
      api.clearAuthToken();

      // Sign out of Firebase
      if (auth) {
        await signOut(auth);
      }

      user.value = null;
      authState.value = 'unauthenticated';
    } catch (err) {
      error.value = handleAuthError(err);
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // Password Management
  // =============================================================================

  async function resetPassword(email: string): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function updateUserPassword(
    currentPassword: string,
    newPassword: string
  ): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw { code: 'auth/user-not-found', message: 'No user logged in' };
      }

      // Re-authenticate before password change
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // Email Verification
  // =============================================================================

  async function sendVerificationEmail(): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw { code: 'auth/user-not-found', message: 'No user logged in' };
      }

      await sendEmailVerification(currentUser);
      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // Profile Management
  // =============================================================================

  async function updateUserProfile(profile: UserProfile): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw { code: 'auth/user-not-found', message: 'No user logged in' };
      }

      await updateProfile(currentUser, profile);

      // Refresh user data
      user.value = formatUser(currentUser);

      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function updateUserEmail(newEmail: string, password: string): Promise<boolean> {
    if (!auth) {
      error.value = { code: 'auth/not-configured', message: 'Firebase Auth is not configured' };
      return false;
    }

    loading.value = true;
    error.value = null;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw { code: 'auth/user-not-found', message: 'No user logged in' };
      }

      // Re-authenticate before email change
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Update email
      await updateEmail(currentUser, newEmail);

      // Send verification email to new address
      await sendEmailVerification(currentUser);

      // Refresh user data
      user.value = formatUser(currentUser);

      return true;
    } catch (err) {
      error.value = handleAuthError(err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  // =============================================================================
  // Token Management
  // =============================================================================

  async function getIdToken(forceRefresh = false): Promise<string | null> {
    if (!auth) return null;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return null;

      return await currentUser.getIdToken(forceRefresh);
    } catch (err) {
      error.value = handleAuthError(err);
      return null;
    }
  }

  // =============================================================================
  // Utility Methods
  // =============================================================================

  function clearError(): void {
    error.value = null;
  }

  function getErrorMessage(code: string): string {
    return ERROR_MESSAGES[code] ?? 'An unexpected error occurred. Please try again.';
  }

  // =============================================================================
  // Lifecycle
  // =============================================================================

  onMounted(() => {
    setupAuthListener();

    // Listen for session refresh requests from the API client
    sessionRefreshHandler = async () => {
      const success = await refreshBackendSession();
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: success }));
    };
    window.addEventListener('session-refresh-needed', sessionRefreshHandler);
  });

  onUnmounted(() => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (sessionRefreshHandler) {
      window.removeEventListener('session-refresh-needed', sessionRefreshHandler);
      sessionRefreshHandler = null;
    }
  });

  // Initialize auth listener immediately
  setupAuthListener();

  // =============================================================================
  // Return Interface
  // =============================================================================

  return {
    // State
    user,
    authState,
    loading,
    error,
    sessionUser,
    hasBackendSession,

    // Computed
    isAuthenticated,
    isEmailVerified,
    isAnonymous,
    userDisplayName,
    userEmail,
    userPhotoURL,
    userId,
    isEmailVerificationPending,

    // Auth Methods
    loginWithEmail,
    registerWithEmail,
    loginWithOAuth,
    logout,

    // Backend Session Management
    createBackendSession,
    refreshBackendSession,

    // Password Management
    resetPassword,
    updateUserPassword,

    // Email Verification
    sendVerificationEmail,

    // Profile Management
    updateUserProfile,
    updateUserEmail,

    // Token Management
    getIdToken,

    // Utility
    clearError,
    getErrorMessage,
  };
}
